import { RenderLayer, } from '@skand/three-js-renderer';
import { Ellipsoid } from '@skand/three-js-renderer/bin/private/math';
import { Matrix3, Matrix4, Quaternion, Vector3 } from 'three';
import { PointIcon } from '../../Assets';
import { calculateCentroid, toCartographic, toECEF, ViewerError, ViewerErrorCode, } from '../../Utils';
import { IFC, Model3D, Panorama, PhotoCamera, Sketch, Terrain, Tileset } from './Model';
import { Imagery } from './Model/Imagery';
/**
 * Methods for creating a model.
 */
export class ModelFactory {
    constructor(renderer, meshBatch, meshInstance, labelManager, globeService, signalDirty) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshBatch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "globeService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "signalDirty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.meshBatch = meshBatch;
        this.meshInstance = meshInstance;
        this.labelManager = labelManager;
        this.globeService = globeService;
        this.signalDirty = signalDirty;
    }
    /**
     * Create a model from a configuration structure.
     *
     * @param config
     * @returns
     */
    async create(config) {
        switch (config.type) {
            case 'tileset':
                return this.createTileset(config);
            case 'obj':
                return this.createObj(config);
            case 'ifc':
                return this.createIFC(config);
            case 'dxf':
                return this.createDXF(config);
            case 'kml':
                return this.createKML(config);
            case 'imagery':
                return this.createImagery(config);
            case 'terrain':
                return this.createTerrain(config);
            case 'panorama':
                return this.createPanorama(config);
            case 'sketch':
                return this.createSketch(config);
            case 'photo-camera':
                return this.createPhotoCamera(config);
        }
    }
    /**
     * Geolocate a Model3D.
     *
     * @param model
     * @param spatialReferenceSystem
     */
    geolocateModel(model, spatialReferenceSystem) {
        const center = model.getPositionDefault();
        const defaultPosition = toECEF(center, spatialReferenceSystem);
        const cartographic = toCartographic(defaultPosition);
        const basis = new Matrix3();
        const up = new Vector3();
        const tangent = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(cartographic, basis, up, tangent);
        const rotationMatrix = new Matrix4().setFromMatrix3(basis);
        const quaternion = new Quaternion().setFromRotationMatrix(rotationMatrix);
        const defaultRotation = quaternion
            .premultiply(new Quaternion().setFromAxisAngle(tangent, -Math.PI / 2))
            .premultiply(new Quaternion().setFromAxisAngle(up, -Math.PI / 2));
        model.setPosition(defaultPosition);
        model.setRotation(defaultRotation);
    }
    /**
     * Create a new Tileset model.
     *
     * @param config
     * @returns
     */
    async createTileset(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            let objectDesc;
            if (config.source == 'cesium') {
                objectDesc = {
                    type: 'cesium',
                    assetId: config.assetId,
                };
            }
            else {
                objectDesc = {
                    type: 'direct',
                    url: config.url,
                };
            }
            const object = await objectFactory.createTileset(objectDesc);
            this.renderer.getWorld().addObject(object);
            return new Tileset(object);
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new Obj model.
     *
     * @param config
     * @returns
     */
    async createObj(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            const object = await objectFactory.createObj(config);
            this.renderer.getWorld().addObject(object);
            const model = new Model3D(object);
            if (config.spatialReferenceSystem) {
                this.geolocateModel(model, config.spatialReferenceSystem);
            }
            return model;
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new IFC model.
     *
     * @param config
     * @returns
     */
    async createIFC(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            const object = await objectFactory.createIfc(config);
            this.renderer.getWorld().addObject(object);
            // Place the model correctly from the given spatial reference system.
            // IFCs need special handling for the computation of the default coordinates.
            const model = new IFC(object);
            if (config.spatialReferenceSystem) {
                // Swap the model center y and z coordinates
                const center = model.getPositionDefault();
                [center.y, center.z] = [-center.z, center.y];
                const defaultPosition = toECEF(center, config.spatialReferenceSystem);
                const cartographic = toCartographic(defaultPosition);
                const basis = new Matrix3();
                const up = new Vector3();
                Ellipsoid.WGS_84.rotationBasisForCartographic(cartographic, basis, up);
                const rotationMatrix = new Matrix4().setFromMatrix3(basis);
                const quaternion = new Quaternion().setFromRotationMatrix(rotationMatrix);
                const defaultRotation = quaternion.premultiply(new Quaternion().setFromAxisAngle(up, -Math.PI / 2));
                model.setPosition(defaultPosition);
                model.setRotation(defaultRotation);
            }
            return model;
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new DXF model.
     *
     * @param config
     * @returns
     */
    async createDXF(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            const object = await objectFactory.createDxf(config);
            this.renderer.getWorld().addObject(object);
            const model = new Model3D(object);
            if (config.spatialReferenceSystem) {
                this.geolocateModel(model, config.spatialReferenceSystem);
            }
            return model;
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new KML model.
     *
     * @param config
     * @returns
     */
    async createKML(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            const labelFont = await this.renderer.createFontAsset({
                url: 'https://s3.ap-southeast-2.amazonaws.com/skand.development.nathan.tiler-test-content/fonts/Roboto/Roboto-Black.ttf',
            });
            let objectDesc;
            if (config.source === 'cesium') {
                objectDesc = {
                    type: 'cesium',
                    assetId: config.assetId,
                    cacheKey: config.cacheKey,
                    labelFont,
                };
            }
            else {
                objectDesc = {
                    type: 'direct',
                    url: config.url,
                    cacheKey: config.cacheKey,
                    labelFont,
                };
            }
            const object = await objectFactory.createKml(objectDesc);
            this.renderer.getWorld().addObject(object);
            return new Model3D(object);
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new Imagery model.
     *
     * @param config
     * @returns
     */
    async createImagery(config) {
        try {
            // Define imagery descriptor
            let descriptor;
            if (config.source === 'cesium') {
                descriptor = {
                    type: 'cesium',
                    assetId: config.assetId,
                };
            }
            else {
                descriptor = {
                    type: 'tms',
                    url: config.url,
                };
            }
            // Load the imagery
            const imagery = new Imagery(descriptor, this.globeService);
            await imagery.show();
            return imagery;
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new Terrain model.
     *
     * @param config
     * @returns
     */
    async createTerrain(config) {
        try {
            // Define terrain descriptor
            const descriptor = {
                type: 'cesium',
                assetId: config.assetId,
            };
            // Load the terrain
            const terrain = new Terrain(descriptor, this.globeService);
            await terrain.show();
            return terrain;
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new Panorama model.
     *
     * @param config
     * @returns
     */
    async createPanorama(config) {
        const objectFactory = this.renderer.getObjectFactory();
        const world = this.renderer.getWorld();
        try {
            return new Panorama(config.name, config.url, config.thumbnailUrl, this.meshInstance, objectFactory, world, this.labelManager);
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    /**
     * Create a new sketch model.
     *
     * @param config
     */
    async createSketch(config) {
        const objectFactory = this.renderer.getObjectFactory();
        try {
            const centroid = calculateCentroid(config.points);
            const vertices = config.points.map(point => point.clone().sub(centroid));
            // Create point sprites
            const sprites = [];
            for (const point of vertices) {
                const spriteDesc = {
                    url: PointIcon,
                    position: {
                        ecef: point,
                    },
                    material: {
                        type: 'basicOpaque',
                        color: config.color,
                    },
                    sizeAttenuation: false,
                    scale: new Vector3(0.03, 0.03, 0.03),
                };
                const sprite = await objectFactory.createSprite(spriteDesc);
                sprite.setRenderLayer(RenderLayer.AfterPanorama);
                sprite.setVisible(false);
                this.renderer.getWorld().addObject(sprite);
                sprites.push(sprite);
            }
            return new Sketch(config.name, config.geometry, config.color, vertices, centroid, sprites, this.meshBatch, this.labelManager, this.signalDirty);
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
    async createPhotoCamera(config) {
        try {
            return new PhotoCamera(config.color, config.pixelSize, this.meshInstance);
        }
        catch (error) {
            throw new ViewerError(ViewerErrorCode.FailedLoad, error.message);
        }
    }
}
